import { createSelector } from 'reselect';
import { CONTAINER_OPTIONS } from 'Constant';
import { initialState } from './reducer';
import findIndex from 'Helper/findIndex';
import {
    CAPACITY_KEY,
    COLOR_KEY,
} from './constants';
import { selectDevice } from '../DeviceDetail/selectors';
import arrayToHashmap from 'Helper/arrayToHashmap';
import merge from 'lodash.merge';

const selectOptionsContainerState = (state) => state[CONTAINER_OPTIONS] || initialState;

const selectAtomicId = () => createSelector(selectOptionsContainerState, state => state.atomicId);

const selectCurrentColor = () => createSelector(selectOptionsContainerState, state => state.currentColor);

const selectCurrentCapacity = () => createSelector(selectOptionsContainerState, state => Number(state.currentCapacity));

const selectColors = () => createSelector(selectDevice(), device => device.colors);

const selectCapacities = () => createSelector(selectDevice(), device => device.capacities);

const selectDefaultCapacity = () => createSelector(selectCapacities(), (capacities) => capacities.filter(c => c.default)[0]);

const selectDefaultSize = () => createSelector( selectDefaultCapacity(), (defaultCapacity) => defaultCapacity?.[CAPACITY_KEY]);

const selectForceRerender = () => createSelector(selectOptionsContainerState, state => state.forceRerender);

const selectImages = () =>
    createSelector(
        selectColors(), selectCurrentColor(),
        (colors, color) => {
            if (!color) {
                return [];
            }
            const index = findIndex(colors, COLOR_KEY, color);
            const atomicDevice = colors[index].capacities[0];

            return atomicDevice.images;
        },
    );

const selectCapacitiesForColor = () =>
    createSelector(
        selectColors(), selectCurrentColor(),
        (colors, color) => {
            if (!color) {
                return [];
            }
            const colorIndex = findIndex(colors, COLOR_KEY, color);
            const filteredCapacities = colors[colorIndex].capacities.filter(c => c.addon?.available === false);

            return filteredCapacities;
        },
    );

const selectSizesForColor = () =>
    createSelector(
        selectCapacitiesForColor(),
        (capacities) => {
            // @TODO: We have to remove duplicate sizes manually till addon data is removed from API
            const allSizes = capacities ? capacities.map(capacity => capacity[CAPACITY_KEY]) : false;
            const uniqSizes = allSizes ? [ ...new Set(allSizes) ] : false;

            return uniqSizes;
        },
    );

const selectAtomicDevice = () =>
    createSelector(
        selectDevice(), selectCurrentColor(), selectCurrentCapacity(),
        (device, color, capacity) => {
            if (!color || !capacity) {
                return false;
            }
            const filteredColor = device.colors.filter(c => c.name === color)[0];
            const filteredCapacity = filteredColor?.capacities.filter(c => c[CAPACITY_KEY] === capacity);

            return filteredCapacity?.filter(c => c.addon?.available === false)[0];
        },
    );

const selectTechnicalDetails = () =>
    createSelector(
        selectDevice(), selectAtomicDevice(),
        (device, atomic) => atomic ? merge(
            arrayToHashmap(merge([], device.technicalDetails), ['section', 'attribute'], true),
            arrayToHashmap(merge([], atomic.technicalDetails) || [], ['section', 'attribute'], true),
        ) : false,
    );

const selectDeliveryScope = () =>
    createSelector(selectAtomicDevice(), (atomic) => atomic ? atomic.deliveryScope : false);

const selectPrice = () =>
    createSelector(selectAtomicDevice(), (atomic) => atomic ? (({oneTimePrice, totalPrice}) => ({oneTimePrice, totalPrice}))(atomic) : false);

const selectDeliveryDate = () =>
    createSelector(selectAtomicDevice(), (atomic) => atomic ? (({shippingDate, hwOnlyShippingDate}) => ({shippingDate, hwOnlyShippingDate}))(atomic) : false);

const selectOptionsByAtomicId = (atomicId) =>
    createSelector(
        selectColors(),
        (colors) => {
            const colorObj = colors.filter(color => color.capacities.filter(c => c.atomicId === atomicId)[0])[0];
            const capacityObj = colorObj ? colorObj.capacities.filter(c => c.atomicId === atomicId)[0] : null;

            const color = colorObj ? colorObj.name : null;
            const capacity = capacityObj ? capacityObj[CAPACITY_KEY] : null;

            return {color, capacity};
        },
    );

const selectCellular = () =>
    createSelector(
        selectAtomicDevice(),
        (device: any) => device?.cellular,
    );

export {
    selectAtomicId,
    selectCapacities,
    selectDefaultCapacity,
    selectDefaultSize,
    selectCurrentColor,
    selectCurrentCapacity,
    selectColors,
    selectCapacitiesForColor,
    selectSizesForColor,
    selectImages,
    selectPrice,
    selectTechnicalDetails,
    selectDeliveryScope,
    selectDeliveryDate,
    selectAtomicDevice,
    selectOptionsByAtomicId,
    selectForceRerender,
    selectCellular,
};
